import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import logo from '../images/logo1.png';
import './styles/menu.css';
import { getDoc, doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useCustomerSwitch } from '../contexts/CustomerSwitchContext';
import { useLanguage } from '../contexts/LanguageContext';

const Menu = ({ onLogout }) => {
  const location = useLocation();
  const { userType } = useAuth(); // Hent userType fra AuthContext
  const { 
    assignedCustomers, 
    selectedCustomer, 
    switchToCustomer, 
    loading: customersLoading,
    isWorkingOnBehalfOfCustomer
  } = useCustomerSwitch();
  const { t } = useLanguage(); // Brug sprogkonteksten
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [userName, setUserName] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [themeLoading, setThemeLoading] = useState(true);
  const [isCustomerSelectorOpen, setIsCustomerSelectorOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // Luk mobilmenuen når skærmen bliver større
      if (!mobile && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Hent brugerdata og tema-indstillinger
  useEffect(() => {
    const fetchUserData = async () => {
      if (auth.currentUser) {
        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserName(userData.name || auth.currentUser.email);
          
          // Hent tema-indstilling hvis den findes
          if (userData.hasOwnProperty('darkMode')) {
            setIsDarkMode(userData.darkMode);
            applyTheme(userData.darkMode);
          } else {
            // Standardværdi er dark mode hvis det ikke findes
            setIsDarkMode(true);
            applyTheme(true);
          }
        } else {
          setUserName(auth.currentUser.email || 'Bruger');
        }
        setThemeLoading(false);
      }
    };
    
    fetchUserData();
  }, []);

  useEffect(() => {
    const checkSubscription = async () => {
      if (!auth.currentUser) return;
      
      try {
        const response = await fetch(
          `${process.env.REACT_APP_STRIPE_CHECK_SUBSCRIPTION_URL}/${auth.currentUser.uid}?email=${encodeURIComponent(auth.currentUser.email)}`
        );
        
        if (!response.ok) {
          throw new Error(`Server svarede med status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Abonnementsstatus:', data);
        setSubscriptionData(data);
      } catch (error) {
        console.error('Fejl ved hentning af abonnementsdata:', error);
      }
    };

    checkSubscription();
  }, []);

  // Lyt på route-ændringer for at lukke mobil-menuen, men kun når der navigeres til et underpunkt
  useEffect(() => {
    // Vi laver en funktion der tjekker om den nuværende rute er et underpunkt
    const isSubItem = () => {
      for (const item of menuItems) {
        if (item.subItems) {
          for (const subItem of item.subItems) {
            if (subItem.path === location.pathname) {
              return true;
            }
          }
        }
      }
      return false;
    };

    // Kun luk menuen hvis vi navigerer til et underpunkt, ikke hovedmenuen
    if (isSubItem()) {
      setIsMobileMenuOpen(false);
    }
  }, [location.pathname]);

  // Alle menupunkter - filtret baseret på userType senere
  const allMenuItems = [
    { path: '/', label: t('menu', 'menuItems.dashboard'), icon: 'dashboard', active: true, allowCustomer: false, hideWhenWorkingForCustomer: false },
    { 
      path: '/linkedin-posts',
      label: t('menu', 'menuItems.linkedInPosts'), 
      icon: 'linkedin', 
      active: true,
      allowCustomer: false, // Hovedmenuen tillades ikke for kunder
      hideWhenWorkingForCustomer: false,
      subItems: [
        { path: '/ai-generator', label: t('menu', 'menuItems.aiGenerator'), icon: 'magic', active: true, allowCustomer: false, hideWhenWorkingForCustomer: false },
        { path: '/case-generator', label: t('menu', 'menuItems.caseGenerator'), icon: 'sticky-note', active: true, allowCustomer: false, hideWhenWorkingForCustomer: false },
        { path: '/linkedin-posts', label: t('menu', 'menuItems.myPosts'), icon: 'calendar-alt', active: true, allowCustomer: false, hideWhenWorkingForCustomer: false },
        { path: '/billedearkiv', label: t('menu', 'menuItems.imageArchive'), icon: 'images', active: true, allowCustomer: false, hideWhenWorkingForCustomer: false },
        { path: '/scheduler', label: t('menu', 'menuItems.scheduler'), icon: 'clock', active: true, allowCustomer: false, hideWhenWorkingForCustomer: false },
        { path: '/ai-settings', label: t('menu', 'menuItems.aiSettings'), icon: 'robot', active: true, allowCustomer: false, hideWhenWorkingForCustomer: false },
        { path: '/linkedin-settings', label: t('menu', 'menuItems.settings'), icon: 'cog', active: true, allowCustomer: false, hideWhenWorkingForCustomer: true }
      ]
    },
    { path: '/organization', label: t('menu', 'menuItems.organization'), icon: 'users', active: true, allowCustomer: false, hideWhenWorkingForCustomer: true },
    // Specialpunkt kun for kunder - direkte adgang til LinkedIn-indstillinger
    { path: '/linkedin-settings', label: t('menu', 'menuItems.settings'), icon: 'cog', active: true, allowCustomer: true, hideWhenWorkingForCustomer: true, onlyCustomers: true }
  ];

  // Filtrér menupunkter baseret på brugertype og om man arbejder på vegne af en kunde
  const menuItems = allMenuItems
    .filter(item => {
      // Vis ikke punkter der skal skjules når man arbejder på vegne af kunde
      if (isWorkingOnBehalfOfCustomer && item.hideWhenWorkingForCustomer) {
        return false;
      }
      // Vis ikke punkter der kun er for kunder hvis brugeren ikke er kunde
      if (item.onlyCustomers && userType !== 'customer') {
        return false;
      }
      // Vis ikke punkter der kun er for kunder hvis brugeren ikke er kunde
      return item.allowCustomer || userType !== 'customer';
    })
    .map(item => {
      if (item.subItems) {
        return {
          ...item,
          subItems: item.subItems.filter(subItem => {
            // Vis ikke underpunkter der skal skjules når man arbejder på vegne af kunde
            if (isWorkingOnBehalfOfCustomer && subItem.hideWhenWorkingForCustomer) {
              return false;
            }
            // Vis ikke underpunkter der kun er for kunder hvis brugeren ikke er kunde
            return subItem.allowCustomer || userType !== 'customer';
          })
        };
      }
      return item;
    });

  // Funktion til at anvende temaet
  const applyTheme = (darkMode) => {
    if (darkMode) {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    }
  };

  // Funktion til at skifte tema og gemme i Firestore
  const toggleTheme = async () => {
    if (!auth.currentUser) return;
    
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    applyTheme(newTheme);
    
    try {
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      
      // Opdater kun darkMode-feltet i brugerens dokument
      await setDoc(userDocRef, { darkMode: newTheme }, { merge: true });
      console.log('Temaindstilling gemt i Firestore');
    } catch (error) {
      console.error('Fejl ved gemning af temaindstilling:', error);
    }
  };

  const toggleMobileMenu = () => {
    console.log('Forsøger at togge menu:', !isMobileMenuOpen);
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Ny funktion til at håndtere klik på hovedmenupunkt med undermenuer
  const handleMenuItemClick = (e, item) => {
    if (item.subItems) {
      e.preventDefault(); // Forebyg navigation
      setOpenSubmenu(prev => prev === item.path ? null : item.path); // Toggle submenu
    }
  };

  // Funktion til at håndtere logout
  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  // Funktion til at generere ikon-klasser baseret på menupunkt
  const getIconClass = (icon) => {
    switch (icon) {
      case 'dashboard': return 'fas fa-tachometer-alt';
      case 'linkedin': return 'fab fa-linkedin';
      case 'email': return 'fas fa-envelope';
      case 'support': return 'fas fa-headset';
      case 'calendar': return 'fas fa-calendar-alt';
      case 'clock': return 'fas fa-clock';
      case 'cog': return 'fas fa-cog';
      case 'robot': return 'fas fa-robot';
      case 'users': return 'fas fa-users';
      case 'images': return 'fas fa-images';
      case 'magic': return 'fas fa-magic';
      case 'sticky-note': return 'fas fa-sticky-note';
      default: return 'fas fa-circle';
    }
  };

  // Tjek om et menupunkt eller dets underpunkter er aktive
  const isMenuActive = (item) => {
    const currentPath = location.pathname;
    if (currentPath === item.path) return true;
    if (item.subItems) {
      return item.subItems.some(subItem => currentPath === subItem.path);
    }
    return false;
  };

  const handleProfileClick = () => {
    if (!isCollapsed) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleBillingClick = async () => {
    try {
      if (!auth.currentUser) return;
      
      const response = await fetch(
        `${process.env.REACT_APP_STRIPE_CREATE_PORTAL_SESSION_URL}/${auth.currentUser.uid}?email=${encodeURIComponent(auth.currentUser.email)}`
      );
      
      if (!response.ok) {
        throw new Error(`Server svarede med status: ${response.status}`);
      }
      
      const data = await response.json();
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Fejl ved åbning af kundeportal:', error);
      window.open('https://billing.stripe.com/p/login/test_dR6eW02g14htacgcMM', '_blank');
    }
    
    setIsDropdownOpen(false);
  };

  const handleCustomerSelect = (customerId) => {
    switchToCustomer(customerId);
    setIsCustomerSelectorOpen(false);
  };

  const getDisplayName = (user) => {
    if (!user) return 'Bruger';
    if (user.displayName) return user.displayName;
    if (user.email) return user.email.split('@')[0];
    return 'Bruger';
  };

  const displayName = getDisplayName(auth.currentUser);

  return (
    <>
      {/* Mobil menu knap - skjules når menuen er åben */}
      {(!isMobileMenuOpen || !isMobile) && (
        <button 
          className="menu-mobile-toggle" 
          onClick={() => toggleMobileMenu()}
          aria-label="Menu"
        >
          <i className="fas fa-bars"></i>
        </button>
      )}

      {/* Overlay til at lukke menuen */}
      {isMobileMenuOpen && (
        <div className="menu-overlay visible" onClick={() => toggleMobileMenu()}></div>
      )}

      {/* Menu */}
      <nav className={`app-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="menu-header">
          <div className="logo-container">
            <img src={logo} alt="Nuvik.io Logo" className="menu-logo" />
            <span className="logo-text">Nuvik.io</span>
          </div>
          
          {/* Vis kun lukke-knap inde i menuen på mobil når menuen er åben */}
          {isMobile && isMobileMenuOpen && (
            <button className="menu-toggle" onClick={() => toggleMobileMenu()}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        {/* Kundevælger - kun synlig for medarbejdere med tildelte kunder */}
        {!isCollapsed && userType !== 'customer' && assignedCustomers.length > 0 && (
          <div className="customer-switcher">
            <div 
              className={`current-customer ${isWorkingOnBehalfOfCustomer ? 'on-behalf' : ''}`}
              onClick={() => setIsCustomerSelectorOpen(!isCustomerSelectorOpen)}
            >
              <div className="customer-icon">
                {isWorkingOnBehalfOfCustomer ? (
                  <i className="fas fa-user-friends"></i>
                ) : (
                  <i className="fas fa-user"></i>
                )}
              </div>
              <div className="customer-info">
                <span className="customer-label">{t('menu', 'customerSwitcher.workingAs')}</span>
                <span className="customer-name">
                  {isWorkingOnBehalfOfCustomer 
                    ? (selectedCustomer.name || selectedCustomer.email)
                    : t('menu', 'customerSwitcher.yourself')}
                </span>
              </div>
              <i className={`fas fa-chevron-${isCustomerSelectorOpen ? 'up' : 'down'}`}></i>
            </div>
            
            {isCustomerSelectorOpen && (
              <div className="customer-dropdown">
                <div 
                  className={`customer-option ${!selectedCustomer ? 'active' : ''}`}
                  onClick={() => handleCustomerSelect(null)}
                >
                  <i className="fas fa-user"></i>
                  <span>{t('menu', 'customerSwitcher.yourself')}</span>
                </div>
                <div className="customer-divider"></div>
                {assignedCustomers.map(customer => (
                  <div 
                    key={customer.id}
                    className={`customer-option ${selectedCustomer?.id === customer.id ? 'active' : ''}`}
                    onClick={() => handleCustomerSelect(customer.id)}
                  >
                    <i className="fas fa-user-friends"></i>
                    <span>{customer.name || customer.email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="menu-items">
          {/* Hvis vi arbejder på vegne af en kunde, vis et banner */}
          {!isCollapsed && isWorkingOnBehalfOfCustomer && (
            <div className="on-behalf-banner">
              <i className="fas fa-info-circle"></i>
              <span>{t('menu', 'customerSwitcher.onBehalfBanner', { customerName: selectedCustomer.name || selectedCustomer.email })}</span>
            </div>
          )}
          
          {menuItems.map((item) => (
            <div key={item.path} className="menu-item-container">
              <NavLink
                to={item.path}
                className={({ isActive }) => 
                  `menu-item ${isMenuActive(item) ? 'active' : ''} ${!item.active ? 'disabled' : ''}`
                }
                end={item.path === '/'}
                onClick={(e) => {
                  if (!item.active) {
                    e.preventDefault();
                  } else if (item.subItems) {
                    handleMenuItemClick(e, item);
                  }
                }}
              >
                <i className={getIconClass(item.icon)}></i>
                {!isCollapsed && (
                  <>
                    <span className="menu-label">{item.label}</span>
                  </>
                )}
              </NavLink>
              
              {/* Vis underpunkter hvis de findes og menuen ikke er kollapset */}
              {!isCollapsed && item.subItems && (openSubmenu === item.path || isMenuActive(item)) && (
                <div className="sub-menu">
                  {item.subItems.map(subItem => (
                    <NavLink
                      key={subItem.path}
                      to={subItem.path}
                      className={({ isActive }) => 
                        `sub-menu-item ${isActive ? 'active' : ''}`
                      }
                    >
                      <i className={getIconClass(subItem.icon)}></i>
                      <span className="menu-label">{subItem.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="menu-footer">
          {/* Theme Toggle Button */}
          {!isCollapsed && (
            <button 
              className="theme-toggle-button"
              onClick={toggleTheme}
              disabled={themeLoading}
              aria-label={isDarkMode ? "Skift til lyst tema" : "Skift til mørkt tema"}
            >
              <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}></i>
              <span>{isDarkMode ? t('menu', 'theme.lightTheme') : t('menu', 'theme.darkTheme')}</span>
            </button>
          )}
          
          <div className={`user-profile ${isDropdownOpen ? 'active' : ''}`} onClick={handleProfileClick}>
            <div className="avatar">
              <i className="fas fa-user"></i>
            </div>
            {!isCollapsed && (
              <div className="user-info">
                <p className="user-name">{displayName}</p>
                {subscriptionData?.hasAccess && (
                  <span className="user-plan-badge">
                    {subscriptionData?.planName ? subscriptionData.planName.split(' ').pop() : 'Basic'}
                  </span>
                )}
              </div>
            )}
          </div>
          
          {!isCollapsed && isDropdownOpen && (
            <div className="user-dropdown">
              <button className="dropdown-item" onClick={handleBillingClick}>
                <i className="fas fa-credit-card"></i>
                <span>{t('menu', 'userMenu.billing')}</span>
              </button>
              <button className="dropdown-item" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt"></i>
                <span>{t('menu', 'userMenu.logout')}</span>
              </button>
            </div>
          )}
          
          {isCollapsed && (
            <>
              <button 
                className="theme-icon"
                onClick={toggleTheme}
                disabled={themeLoading}
                aria-label={isDarkMode ? "Skift til lyst tema" : "Skift til mørkt tema"}
              >
                <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}></i>
              </button>
              <button className="logout-icon" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </>
          )}
        </div>
      </nav>
    </>
  );
};

export default Menu;
